/**
 * Listing Wizard utilities.
 *
 * Guided step-by-step listing creation with progress tracking,
 * tips, and completion scoring.
 *
 * Wizard state stored in listing.privateData.wizardProgress = {
 *   completedSteps: string[],
 *   currentStep: string,
 *   completionPct: number
 * }
 *
 * Steps: basic_info, description, photos, pricing, location, availability, publish
 */

export const WIZARD_STEPS = [
  { id: 'basic_info', label: 'Basic info', required: true, weight: 20 },
  { id: 'description', label: 'Description', required: true, weight: 20 },
  { id: 'photos', label: 'Photos', required: true, weight: 25 },
  { id: 'pricing', label: 'Pricing', required: true, weight: 20 },
  { id: 'location', label: 'Location', required: false, weight: 5 },
  { id: 'availability', label: 'Availability', required: false, weight: 5 },
  { id: 'publish', label: 'Review & Publish', required: true, weight: 5 },
];

export const WIZARD_STEP_IDS = WIZARD_STEPS.map(s => s.id);

/**
 * Calculate listing completeness score.
 * @param {string[]} completedSteps
 * @returns {number} — 0-100
 */
export const calculateCompleteness = (completedSteps = []) => {
  if (!Array.isArray(completedSteps)) return 0;
  return WIZARD_STEPS.reduce((total, step) => {
    return total + (completedSteps.includes(step.id) ? step.weight : 0);
  }, 0);
};

/**
 * Get next incomplete step.
 * @param {string[]} completedSteps
 * @returns {Object|null}
 */
export const getNextStep = (completedSteps = []) => {
  return WIZARD_STEPS.find(s => !completedSteps.includes(s.id)) || null;
};

/**
 * Check if listing is ready to publish.
 * @param {string[]} completedSteps
 * @returns {boolean}
 */
export const isReadyToPublish = (completedSteps = []) => {
  const requiredSteps = WIZARD_STEPS.filter(s => s.required).map(s => s.id);
  return requiredSteps.every(s => completedSteps.includes(s));
};

/**
 * Get missing required steps.
 * @param {string[]} completedSteps
 * @returns {Object[]}
 */
export const getMissingRequired = (completedSteps = []) =>
  WIZARD_STEPS.filter(s => s.required && !completedSteps.includes(s.id));
